import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { App, Drawer } from 'antd'
import { useShallow } from 'zustand/react/shallow'
import BrandButton, { brandModalConfirmProps } from '@/components/BrandButton'
import {
  useAppStore,
  useUploadDrawerStore,
  useUploadStore,
  useUserStore,
  useVersionDrawerStore,
} from '@/store'
import type { AppVersion, MainPackageInfo } from '@/types'
import { findSpaceById } from '@/lib/spaces'
import { applyPendingCreateSpaceLock } from '@/lib/createSpaceLock'
import { applyMainUpload } from './applyMainUpload'
import { applyCreateSpaceContext } from './formModel'
import { DrawerCloseExtra, SectionTitle } from './parts'
import MainPackageSection from './MainPackageSection'

const EMPTY_VERSIONS: AppVersion[] = []

export default function UploadPackageDrawer() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const navigate = useNavigate()
  const { app_id: routeAppId } = useParams()
  const {
    open,
    mode,
    app_id,
    version_id,
    space_id,
    space_type,
    lockSpaceContext,
    bindAppId,
    bindVersionId,
    close,
    reset,
  } = useUploadDrawerStore(
    useShallow((s) => ({
      open: s.open,
      mode: s.mode,
      app_id: s.app_id,
      version_id: s.version_id,
      space_id: s.space_id,
      space_type: s.space_type,
      lockSpaceContext: s.lockSpaceContext,
      bindAppId: s.bindAppId,
      bindVersionId: s.bindVersionId,
      close: s.close,
      reset: s.reset,
    })),
  )

  const openVersion = useVersionDrawerStore((s) => s.openVersion)
  const dropMainTasks = useUploadStore((s) => s.dropMainTasks)

  const {
    mainParse,
    draft,
    draft_app_id,
    draft_version_id,
    setDraftVersionId,
    setDraftAppId,
    hasUploading,
  } = useUploadStore(
    useShallow((s) => ({
      mainParse: s.mainParse,
      draft: s.draft,
      draft_app_id: s.draft_app_id,
      draft_version_id: s.draft_version_id,
      setDraftVersionId: s.setDraftVersionId,
      setDraftAppId: s.setDraftAppId,
      hasUploading: s.tasks.some(
        (x) =>
          x.kind === 'main' &&
          (x.status === 'uploading' || x.status === 'parsing'),
      ),
    })),
  )

  const app = useAppStore((s) => (app_id ? s.getApp(app_id) : undefined))
  const versions = useAppStore((s) =>
    app_id ? s.versions[app_id] ?? EMPTY_VERSIONS : EMPTY_VERSIONS,
  )
  const ensureRemoteDraft = useAppStore((s) => s.ensureRemoteDraft)
  const spaces = useUserStore((s) => s.spaces)
  const resolvedSpaceType =
    space_type ?? findSpaceById(spaces, space_id)?.type ?? null

  const version = (version_id ?? draft_version_id)
    ? versions.find((v) => v.id === (version_id ?? draft_version_id))
    : undefined

  const handledParseKey = useRef<MainPackageInfo | null>(null)

  const tasks = useUploadStore((s) => s.tasks)
  const toastedFailures = useRef(new Set<string>())

  useEffect(() => {
    if (!open) {
      toastedFailures.current.clear()
      return
    }
    if (tasks.length === 0) {
      toastedFailures.current.clear()
      return
    }
    for (const task of tasks) {
      if (task.status !== 'failed' && task.status !== 'parse_failed') continue
      const key = `${task.id}:${task.error_message ?? ''}`
      if (toastedFailures.current.has(key)) continue
      toastedFailures.current.add(key)
      message.error(
        task.error_message?.trim() || t('versionForm.uploadFailed'),
      )
    }
  }, [open, tasks, message, t])

  useEffect(() => {
    if (!open) return
    if (app_id) setDraftAppId(app_id)
    if (mode === 'edit' && version_id) setDraftVersionId(version_id)
  }, [open, mode, app_id, version_id, setDraftAppId, setDraftVersionId])

  // 主包上传成功 → 以响应 draft 为真相写入本地缓存
  useEffect(() => {
    if (!open || !mainParse || !draft || !mode) return
    if (!draft_app_id || !draft_version_id) return

    // 认这一次响应对象，同名同版本号再传也会写入
    if (handledParseKey.current === mainParse) return

    const local = useAppStore
      .getState()
      .versions[draft_app_id]?.find((v) => v.id === draft_version_id)
    const next = applyMainUpload(local, draft, mainParse, draft_version_id)
    if (next.is_first_version == null && mode === 'createApp') {
      next.is_first_version = true
    }
    if (mode === 'createApp') {
      const app_fixed_info = applyCreateSpaceContext(draft.app_fixed_info, {
        space_id,
        lockSpaceContext,
        space_type: resolvedSpaceType,
      })
      if (app_fixed_info) next.app_fixed_info = app_fixed_info
    }
    const locked = applyPendingCreateSpaceLock(draft_app_id, next)

    ensureRemoteDraft({
      app_id: draft_app_id,
      icon: mainParse.icon,
      replacePlatformPackages: true,
      version: locked,
    })
    bindAppId(draft_app_id)
    bindVersionId(draft_version_id)
    // 侧栏读本地缓存，版本表要重拉才会出现这份新草稿
    useAppStore.getState().bumpVersionsListRev()
    handledParseKey.current = mainParse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mainParse,
    draft,
    draft_app_id,
    draft_version_id,
    open,
    mode,
    space_id,
    resolvedSpaceType,
    lockSpaceContext,
  ])

  useEffect(() => {
    if (!open) handledParseKey.current = null
  }, [open])

  const committed = version?.main_package ?? mainParse ?? undefined
  const effectiveAppId = app_id ?? draft_app_id
  const effectiveVersionId = version_id ?? draft_version_id

  const drawerBg = committed
    ? 'var(--color-background-module-muted)'
    : 'var(--color-background-module-container)'

  const title =
    mode === 'createApp'
      ? t('versionForm.uploadCreateAppTitle')
      : mode === 'newVersion'
        ? t('versionForm.createTitle')
        : t('versionForm.uploadTitle')

  const canGoConfigure =
    !!effectiveAppId &&
    !!effectiveVersionId &&
    !!committed &&
    !hasUploading

  const goConfigure = () => {
    if (!effectiveAppId || !effectiveVersionId) return
    dropMainTasks()
    close()
    if (routeAppId !== effectiveAppId) {
      navigate(`/apps/${effectiveAppId}/versions`)
    }
    openVersion(effectiveAppId, effectiveVersionId)
  }

  /** 主包不走后台上传：上传中关闭需确认，确认后中断并清空上传态 */
  const handleClose = () => {
    if (!hasUploading) {
      dropMainTasks()
      close()
      return
    }
    modal.confirm({
      title: t('versionForm.closeWhileUploadingTitle'),
      content: t('versionForm.closeWhileUploadingContent'),
      okText: t('versionForm.closeWhileUploadingOk'),
      cancelText: t('common.cancel'),
      ...brandModalConfirmProps('danger'),
      onOk: () => {
        // 确认框打开期间上传可能已结束：保留结果，让用户直接去配置
        const stillUploading = useUploadStore
          .getState()
          .tasks.some(
            (x) =>
              x.kind === 'main' &&
              (x.status === 'uploading' || x.status === 'parsing'),
          )
        if (!stillUploading) {
          message.info(t('versionForm.uploadFinishedBeforeClose'))
          return
        }
        dropMainTasks()
        close()
      },
    })
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      afterOpenChange={(visible) => {
        // 关闭动画结束后再清 mode；若期间又被打开则跳过，避免竞态
        if (!visible && !useUploadDrawerStore.getState().open) reset()
      }}
      width={614}
      title={
        <span style={{ fontWeight: 500, fontSize: 18, lineHeight: '25px' }}>
          {title}
        </span>
      }
      closable={false}
      extra={<DrawerCloseExtra onClose={handleClose} />}
      destroyOnHidden
      styles={{
        body: {
          padding: 0,
          background: drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
        content: {
          background: drawerBg,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
        },
        header: {
          background: drawerBg,
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
          padding: '20px',
        },
      }}
    >
      <div
        style={{
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          flex: 1,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionTitle>{t('versionForm.mainPackage')}</SectionTitle>
          <MainPackageSection
            app={app}
            committed={committed}
            readOnly={false}
            uploadContext={{
              app_id: effectiveAppId ?? undefined,
              version_id: effectiveVersionId ?? undefined,
            }}
          />
        </div>
      </div>

      {canGoConfigure && (
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--color-border-line)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <BrandButton onClick={goConfigure}>
            {t('uploadPanel.configure')}
          </BrandButton>
        </div>
      )}
    </Drawer>
  )
}
